import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged } from 'firebase/auth';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  onSnapshot,
  query,
  where,
  orderBy,
  arrayUnion,
  serverTimestamp 
} from 'firebase/firestore';
import WorkItemCard from './components/WorkItemCard';
import WorkItemModal from './components/WorkItemModal';
import AddItemModal from './components/AddItemModal';

function App() {
  const [user, setUser] = useState(null);
  const [workItems, setWorkItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [availableTags, setAvailableTags] = useState(['LEAP', 'FES', 'TPT', 'DATA', 'AI', 'Evolve', 'RDB', 'Apps', 'Edplace']);
  
  // Filter and grouping states
  const [filterTypes, setFilterTypes] = useState(['Task']);
  const [filterDivisions, setFilterDivisions] = useState([]);
  const [filterTags, setFilterTags] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [relatedItemIds, setRelatedItemIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('board'); // 'board' or 'todo'
  const [dragOverItemId, setDragOverItemId] = useState(null);
  const [controlsExpanded, setControlsExpanded] = useState(true);

  const AVAILABLE_TAGS = ['LEAP', 'FES', 'TPT', 'DATA', 'AI', 'Evolve', 'RDB', 'Apps', 'Edplace'];

  const toggleTypeFilter = (type) => {
    setFilterTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const toggleDivisionFilter = (division) => {
    setFilterDivisions(prev => 
      prev.includes(division) 
        ? prev.filter(d => d !== division)
        : [...prev, division]
    );
  };

  const toggleTagFilter = (tag) => {
    setFilterTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const resetFilters = () => {
    setFilterTypes([]);
    setFilterDivisions([]);
    setFilterTags([]);
    setFilterStatus('all');
    setRelatedItemIds([]);
    setSearchQuery('');
  };

  const handleShowRelatedItems = (item) => {
    const relatedIds = new Set();
    
    // Always include the current item
    relatedIds.add(item.id);
    
    if (item.type === 'Initiative') {
      // Show all children and grandchildren
      const children = workItems.filter(wi => wi.parentId === item.id);
      children.forEach(child => {
        relatedIds.add(child.id);
        // Add grandchildren (children of this child)
        const grandchildren = workItems.filter(wi => wi.parentId === child.id);
        grandchildren.forEach(gc => relatedIds.add(gc.id));
      });
    } else if (item.type === 'Epic') {
      // Show parent and children
      if (item.parentId) {
        relatedIds.add(item.parentId);
      }
      const children = workItems.filter(wi => wi.parentId === item.id);
      children.forEach(child => relatedIds.add(child.id));
    } else if (item.type === 'Task') {
      // Show siblings (including this task), parent, and grandparent
      if (item.parentId) {
        relatedIds.add(item.parentId); // parent
        const siblings = workItems.filter(wi => wi.parentId === item.parentId);
        siblings.forEach(sibling => relatedIds.add(sibling.id));
        
        // Find grandparent
        const parent = workItems.find(wi => wi.id === item.parentId);
        if (parent && parent.parentId) {
          relatedIds.add(parent.parentId);
        }
      }
    }
    
    // Reset other filters and set related items
    setFilterTypes([]);
    setFilterDivisions([]);
    setFilterTags([]);
    setFilterStatus('all');
    setSearchQuery('');
    setRelatedItemIds(Array.from(relatedIds));
    setSelectedItem(null); // Close modal
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) {
      setWorkItems([]);
      return;
    }

    const q = query(
      collection(db, 'workItems'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setWorkItems(items);
      
      // Extract all unique tags from all items
      const allTags = new Set(availableTags);
      items.forEach(item => {
        if (item.tags) {
          item.tags.forEach(tag => allTags.add(tag));
        }
      });
      setAvailableTags(Array.from(allTags));
      
      // Auto-archive completed items older than 2 weeks (once per day max)
      const lastArchiveCheck = localStorage.getItem('lastArchiveCheck');
      const today = new Date().toDateString();
      
      if (lastArchiveCheck !== today) {
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        
        items.forEach(item => {
          if (item.status === 'Complete' && item.completedAt && !item.archived) {
            const completedDate = item.completedAt.toDate ? item.completedAt.toDate() : new Date(item.completedAt);
            if (completedDate < twoWeeksAgo) {
              // Mark as archived
              updateDoc(doc(db, 'workItems', item.id), { archived: true }).catch(err => 
                console.error('Error archiving item:', err)
              );
            }
          }
        });
        
        // Update last check date
        localStorage.setItem('lastArchiveCheck', today);
      }
    });

    return unsubscribe;
  }, [user]);

  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Login error:', error);
      alert('Failed to sign in. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleAddItem = async (itemData) => {
    try {
      await addDoc(collection(db, 'workItems'), {
        ...itemData,
        userId: user.uid,
        createdAt: serverTimestamp(),
        lastNoteDate: serverTimestamp(),
        tags: itemData.tags || [],
        links: itemData.links || [],
        parentId: itemData.parentId || null,
        parentType: itemData.parentType || null,
        todoListOrder: null // Will be set when user reorders in todo view
      });
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Failed to add item. Please try again.');
    }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickAddTitle.trim()) return;

    await handleAddItem({
      title: quickAddTitle,
      type: 'Task',
      division: '',
      status: 'Open',
      description: ''
    });
    
    setQuickAddTitle('');
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      const itemRef = doc(db, 'workItems', itemId);
      await updateDoc(itemRef, {
        ...updates,
        lastNoteDate: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item. Please try again.');
    }
  };

  const handleTodoReorder = async (draggedId, targetId) => {
    // Get the dragged and target items
    const draggedItem = workItems.find(item => item.id === draggedId);
    const targetItem = workItems.find(item => item.id === targetId);
    
    if (!draggedItem || !targetItem) return;

    // Get all todo items sorted by current order
    const todoItems = getTodoItems();
    const draggedIndex = todoItems.findIndex(item => item.id === draggedId);
    const targetIndex = todoItems.findIndex(item => item.id === targetId);

    // Reorder the array
    const reordered = [...todoItems];
    reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, draggedItem);

    // Update todoListOrder for all items
    const updates = reordered.map((item, index) => 
      updateDoc(doc(db, 'workItems', item.id), { todoListOrder: index })
    );

    try {
      await Promise.all(updates);
    } catch (error) {
      console.error('Error reordering items:', error);
    }
  };

  const handleDragStart = (e, item) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('itemId', item.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const itemId = e.dataTransfer.getData('itemId');
    const item = workItems.find(i => i.id === itemId);
    
    if (item && item.status !== newStatus) {
      const updates = { status: newStatus };
      
      // Set completedAt when moving to Complete status
      if (newStatus === 'Complete' && item.status !== 'Complete') {
        updates.completedAt = serverTimestamp();
      }
      
      // Clear completedAt when moving away from Complete
      if (newStatus !== 'Complete' && item.status === 'Complete') {
        updates.completedAt = null;
        updates.archived = false;
      }
      
      await handleUpdateItem(itemId, updates);
    }
  };

  const getFilteredItems = () => {
    return workItems.filter(item => {
      // Filter out archived items unless showArchived is true
      if (item.archived && !showArchived) return false;
      
      // If related items filter is active, only show related items
      if (relatedItemIds.length > 0 && !relatedItemIds.includes(item.id)) return false;
      
      // Search filter - check title and description
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const titleMatch = item.title?.toLowerCase().includes(query);
        const descriptionMatch = item.description?.toLowerCase().includes(query);
        if (!titleMatch && !descriptionMatch) return false;
      }
      
      if (item.status === 'Done') return false;
      if (filterTypes.length > 0 && !filterTypes.includes(item.type)) return false;
      if (filterDivisions.length > 0 && !filterDivisions.includes(item.division)) return false;
      if (filterTags.length > 0) {
        const itemTags = item.tags || [];
        const hasMatchingTag = filterTags.some(tag => itemTags.includes(tag));
        if (!hasMatchingTag) return false;
      }
      if (filterStatus !== 'all' && item.status !== filterStatus) return false;
      return true;
    });
  };

  const getGroupedItems = () => {
    const filtered = getFilteredItems();
    const grouped = {
      'Open': [],
      'In Progress': [],
      'Blocked': [],
      'Complete': []
    };

    filtered.forEach(item => {
      const status = item.status || 'Open';
      if (grouped[status]) {
        grouped[status].push(item);
      }
    });

    // Sort items within each group by lastNoteDate (oldest first)
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        const dateA = a.lastNoteDate?.toDate?.() || new Date(0);
        const dateB = b.lastNoteDate?.toDate?.() || new Date(0);
        return dateA - dateB;
      });
    });

    return grouped;
  };

  const getTodoItems = () => {
    return workItems
      .filter(item => {
        // Basic todo list filters
        if (item.type !== 'Task') return false;
        if (item.status !== 'Open' && item.status !== 'In Progress' && item.status !== 'Blocked') return false;
        if (item.archived && !showArchived) return false;
        
        // Search filter - check title and description
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          const titleMatch = item.title?.toLowerCase().includes(query);
          const descriptionMatch = item.description?.toLowerCase().includes(query);
          if (!titleMatch && !descriptionMatch) return false;
        }
        
        return true;
      })
      .sort((a, b) => {
        // Sort by todoListOrder, fallback to lastNoteDate
        const orderA = a.todoListOrder ?? 999999;
        const orderB = b.todoListOrder ?? 999999;
        if (orderA !== orderB) return orderA - orderB;
        
        const dateA = a.lastNoteDate?.toDate?.() || new Date(0);
        const dateB = b.lastNoteDate?.toDate?.() || new Date(0);
        return dateA - dateB;
      });
  };

  if (loading) {
    return (
      <div className="app">
        <div className="login-container">
          <div className="login-box">
            <h1>Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app">
        <div className="login-container">
          <div className="login-box">
            <h1>Organiser</h1>
            <button onClick={handleLogin} className="login-button">
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
                <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
              </svg>
              Sign in with Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  const groupedItems = getGroupedItems();

  return (
    <div className="app">
      <header className="header">
        <h1>Organiser</h1>
        <div className="header-controls">
          <div className="user-info">
            <span className="user-email">{user.email}</span>
          </div>
          <button onClick={handleLogout} className="logout-button">
            Sign Out
          </button>
        </div>
      </header>

      <div className="controls">
        {/* Collapsible Filters Section */}
        {controlsExpanded && (
        <div className="controls-row" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
          <div className="control-group-vertical">
            <div className="control-group">
              <label>Type:</label>
              <div className="tag-chips-container">
                <button
                  onClick={() => toggleTypeFilter('Task')}
                  className={`tag-chip tag-chip-task ${filterTypes.includes('Task') ? 'tag-chip-active' : ''}`}
                >
                  Task
                </button>
                <button
                  onClick={() => toggleTypeFilter('Epic')}
                  className={`tag-chip tag-chip-epic ${filterTypes.includes('Epic') ? 'tag-chip-active' : ''}`}
                >
                  Epic
                </button>
                <button
                  onClick={() => toggleTypeFilter('Initiative')}
                  className={`tag-chip tag-chip-initiative ${filterTypes.includes('Initiative') ? 'tag-chip-active' : ''}`}
                >
                  Initiative
                </button>
              </div>
            </div>

            <div className="control-group">
              <label>Division:</label>
              <div className="tag-chips-container">
                <button
                  onClick={() => toggleDivisionFilter('S&I')}
                  className={`tag-chip ${filterDivisions.includes('S&I') ? 'tag-chip-active' : ''}`}
                >
                  S&I
                </button>
                <button
                  onClick={() => toggleDivisionFilter('R&S')}
                  className={`tag-chip ${filterDivisions.includes('R&S') ? 'tag-chip-active' : ''}`}
                >
                  R&S
                </button>
                <button
                  onClick={() => toggleDivisionFilter('Common')}
                  className={`tag-chip ${filterDivisions.includes('Common') ? 'tag-chip-active' : ''}`}
                >
                  Common
                </button>
              </div>
            </div>
          </div>

          <div className="control-group" style={{ flex: 1 }}>
            <label>Tags:</label>
            <div className="tag-chips-container" style={{ maxWidth: '100%' }}>
              {availableTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTagFilter(tag)}
                  className={`tag-chip ${filterTags.includes(tag) ? 'tag-chip-active' : ''}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="control-group-vertical" style={{ alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <span>Show Archived</span>
              <div 
                className={`toggle-switch ${showArchived ? 'toggle-on' : ''}`}
                onClick={() => setShowArchived(!showArchived)}
              >
                <div className="toggle-slider"></div>
              </div>
            </label>
            <button 
              onClick={resetFilters} 
              className="reset-filters-icon rotate-on-hover"
              title="Reset all filters"
              aria-label="Reset all filters"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            </button>
          </div>
        </div>
        )}

        {/* Quick Add and View Selector - Always Visible */}
        <div className="quick-add-and-view-section">
          <form onSubmit={handleQuickAdd} className="quick-add-form">
            <input
              type="text"
              value={quickAddTitle}
              onChange={(e) => setQuickAddTitle(e.target.value)}
              placeholder="Quick add task..."
              className="quick-add-input"
            />
            <button type="submit" className="add-item-button">
              + Add
            </button>
          </form>

          {/* Search Input */}
          <div style={{ position: 'relative', flex: 1, minWidth: '150px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="quick-add-input"
              style={{ width: '100%', paddingRight: searchQuery ? '35px' : '12px' }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="reset-filters-icon"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: '4px'
                }}
                title="Clear search"
                aria-label="Clear search"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => setViewMode('board')}
              className={`view-toggle-button ${viewMode === 'board' ? 'view-toggle-active' : ''}`}
            >
              Board View
            </button>
            <button
              onClick={() => setViewMode('todo')}
              className={`view-toggle-button ${viewMode === 'todo' ? 'view-toggle-active' : ''}`}
            >
              To-Do List
            </button>
            
            {/* Collapse Toggle Icon */}
            <button
              onClick={() => setControlsExpanded(!controlsExpanded)}
              className="collapse-toggle"
              title={controlsExpanded ? 'Hide filters' : 'Show filters'}
              aria-label={controlsExpanded ? 'Hide filters' : 'Show filters'}
              style={{ marginLeft: '0.5rem' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {controlsExpanded ? (
                  <polyline points="18 15 12 9 6 15"></polyline>
                ) : (
                  <polyline points="6 9 12 15 18 9"></polyline>
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="work-items-container swim-lanes">
        {viewMode === 'board' ? (
          // Board View
          Object.keys(groupedItems).length === 0 ? (
            <div className="empty-state">
              <p>No work items yet</p>
              <p>Click "Add Work Item" to get started!</p>
            </div>
          ) : (
            <div className="lanes-wrapper">
              {Object.entries(groupedItems).map(([status, items]) => (
                <div 
                  key={status} 
                  className="swim-lane"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, status)}
                >
                  <h2 className="lane-header">{status} ({items.length})</h2>
                  <div className="lane-items">
                    {items.map(item => {
                      const parentItem = item.parentId 
                        ? workItems.find(wi => wi.id === item.parentId) 
                        : null;
                      
                      return (
                        <WorkItemCard
                          key={item.id}
                          item={item}
                          parentItem={parentItem}
                          onClick={() => setSelectedItem(item)}
                          onParentClick={(parent) => setSelectedItem(parent)}
                          onShowRelatedItems={handleShowRelatedItems}
                          onDragStart={(e) => handleDragStart(e, item)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          // To-Do List View
          <div className="todo-list-container">
            <h2 className="todo-list-header">My Tasks ({getTodoItems().length})</h2>
            {getTodoItems().length === 0 ? (
              <div className="empty-state">
                <p>No tasks in your to-do list</p>
                <p>Tasks with Open, In Progress, or Blocked status will appear here</p>
              </div>
            ) : (
              <div className="todo-list">
                {getTodoItems().map((item, index) => {
                  const parentItem = item.parentId 
                    ? workItems.find(wi => wi.id === item.parentId) 
                    : null;
                  
                  return (
                    <div
                      key={item.id}
                      className={`todo-item ${dragOverItemId === item.id ? 'drag-over' : ''}`}
                      draggable="true"
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('todoItemId', item.id);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.dataTransfer.dropEffect = 'move';
                        setDragOverItemId(item.id);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) {
                          setDragOverItemId(null);
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverItemId(null);
                        const draggedId = e.dataTransfer.getData('todoItemId');
                        if (draggedId && draggedId !== item.id) {
                          handleTodoReorder(draggedId, item.id);
                        }
                      }}
                      onClick={() => setSelectedItem(item)}
                    >
                      <div className="todo-item-content">
                        <WorkItemCard
                          item={item}
                          parentItem={parentItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem(item);
                          }}
                          onParentClick={(parent) => setSelectedItem(parent)}
                          onDragStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          draggable={false}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {selectedItem && (
        <WorkItemModal
          item={workItems.find(item => item.id === selectedItem.id) || selectedItem}
          allItems={workItems}
          availableTags={availableTags}
          onClose={() => setSelectedItem(null)}
          onUpdate={handleUpdateItem}
          onNavigateToItem={(item) => setSelectedItem(item)}
          onShowRelatedItems={handleShowRelatedItems}
        />
      )}
    </div>
  );
}

export default App;
